import { type FC } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Player, type PlayerTimecode } from "@/shared/components/Player";
import { useVideoProviderTextKeys } from "../model";

interface PlayerPreviewProps {
  hlsUrl?: string | null;
  processingStatus?: string | null;
  /** CF-фолбэк — кадр видео, показывается пока у контента нет своего превью-баннера */
  thumbnailUrl?: string | null;
  hasPreview?: boolean;
  /** Сохранённые метки таймлайна — видны и в превью формы редактирования. */
  timecodes?: PlayerTimecode[] | null;
}

export const PlayerPreview: FC<PlayerPreviewProps> = ({
  hlsUrl,
  processingStatus,
  thumbnailUrl,
  hasPreview,
  timecodes,
}) => {
  const { t } = useTranslation();
  const providerTextKeys = useVideoProviderTextKeys();

  if (!hlsUrl) {
    if (processingStatus === "PROCESSING") {
      return (
        <Typography variant="body2" color="text.secondary">
          {t(providerTextKeys.playerPreviewProcessing)}
        </Typography>
      );
    }

    if (thumbnailUrl && !hasPreview) {
      return (
        <Box
          sx={{
            position: "relative",
            pt: "56.25%",
            borderRadius: 1,
            overflow: "hidden",
            bgcolor: "action.hover",
          }}
        >
          <img
            src={thumbnailUrl}
            alt=""
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              left: 4,
              bottom: 4,
              px: 0.5,
              borderRadius: 0.5,
              bgcolor: "rgba(0,0,0,0.6)",
              color: "#fff",
            }}
          >
            {t("videos.playerPreview.thumbnailFallbackCaption")}
          </Typography>
        </Box>
      );
    }

    return null;
  }

  return (
    <Player
      src={hlsUrl}
      poster={thumbnailUrl ?? null}
      autoPlay={false}
      hotkeys={false}
      timecodes={timecodes}
    />
  );
};
